import { ArrowRightLeft, CheckCircle2, Info } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  YStack,
  XStack,
  HeadingProps,
  ParagraphProps,
  Label,
  Circle,
  getTokenValue,
  Token,
} from "tamagui";
import {
  GradientButton,
  GradientButtonProps,
  OutlinedButton,
  OutlinedButtonProps,
  tokens,
} from "tamagui-config";

import { Select } from "../../components/Select";

interface ConfigureIntegrationDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => Promise<void> | void;
  onConfirm: () => Promise<boolean> | void;
  title: string;
  titleProps?: HeadingProps;
  description?: string;
  descriptionProps?: ParagraphProps;
  confirmButtonText?: string;
  confirmButtonProps?: GradientButtonProps;
  cancelButtonText?: string;
  cancelButtonProps?: OutlinedButtonProps;
  onSuccess?: () => void;
  integrationTitle?: string;
}

interface SuccessContentProps {
  onClose: () => void;
  integrationTitle?: string;
}

const SuccessContent = ({ onClose, integrationTitle }: SuccessContentProps) => {
  const { t } = useTranslation();

  const checkmarkGradientStart = getTokenValue(
    "$gradientSuccessCheckmarkStart" as Token,
    "color",
  );
  const checkmarkGradientEnd = getTokenValue(
    "$gradientSuccessCheckmarkEnd" as Token,
    "color",
  );

  return (
    <YStack gap="$5" alignItems="center" width="100%">
      <Circle
        size={120}
        backgroundImage={`linear-gradient(231.68deg, ${checkmarkGradientStart} 34.15%, ${checkmarkGradientEnd} 64.88%)`}
        backgroundOrigin="border-box"
        borderWidth={2}
        borderColor="$transparentBorder"
      >
        <CheckCircle2 size={60} color={tokens.color.successGreen} />
      </Circle>
      <YStack gap="$3" alignItems="center">
        <Dialog.Title fontSize="$3" textAlign="center" lineHeight="$1">
          {t("integrations.successTitle")}
        </Dialog.Title>
        <Dialog.Description textAlign="center" color="$white10">
          {t("integrations.successMessage", {
            integrationName: integrationTitle || "ServiceTitan",
          })}
        </Dialog.Description>
      </YStack>
      <GradientButton rounded onPress={onClose}>
        {t("integrations.done")}
      </GradientButton>
    </YStack>
  );
};

interface ErrorContentProps {
  onRetry: () => void;
  onClose: () => void;
  integrationTitle?: string;
}

const ErrorContent = ({
  onRetry,
  onClose,
  integrationTitle,
}: ErrorContentProps) => {
  const { t } = useTranslation();

  const checkmarkGradientStart = getTokenValue(
    "$gradientSuccessCheckmarkStart" as Token,
    "color",
  );
  const checkmarkGradientEnd = getTokenValue(
    "$gradientSuccessCheckmarkEnd" as Token,
    "color",
  );

  return (
    <YStack gap="$5" alignItems="center" width="100%">
      <Circle
        size={120}
        backgroundImage={`linear-gradient(231.68deg, ${checkmarkGradientStart} 34.15%, ${checkmarkGradientEnd} 64.88%)`}
        backgroundOrigin="border-box"
        borderWidth={2}
        borderColor="$transparentBorder"
      >
        <Info size={60} color={tokens.color.error} />
      </Circle>
      <YStack gap="$3" alignItems="center">
        <Dialog.Title
          fontSize="$3"
          textAlign="center"
          fontWeight="$4"
          lineHeight="$1"
        >
          {t("integrations.errorTitle")}
        </Dialog.Title>
        <Dialog.Description textAlign="center" color="$white10">
          {t("integrations.errorMessage", {
            integrationName: integrationTitle || "ServiceTitan",
          })}
        </Dialog.Description>
      </YStack>
      <XStack gap="$3">
        <OutlinedButton
          flex={1}
          onPress={() => {
            onClose();
          }}
          rounded
        >
          {t("common.cancel")}
        </OutlinedButton>
        <GradientButton flex={1} onPress={onRetry} rounded>
          {t("common.retry")}
        </GradientButton>
      </XStack>
    </YStack>
  );
};

type DialogState = "initial" | "success" | "failure";

export function ConfigureIntegrationDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  titleProps,
  description,
  descriptionProps,
  confirmButtonText,
  confirmButtonProps,
  cancelButtonText,
  cancelButtonProps,
  onSuccess,
  integrationTitle,
}: ConfigureIntegrationDialogProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<DialogState>("initial");

  const [loading, setIsLoading] = useState(false);

  // Reset dialog state when opened
  useEffect(() => {
    if (open) {
      setState("initial");
      setIsLoading(false);
    }
  }, [open]);

  const onConfirmPress = async () => {
    setIsLoading(true);
    try {
      const result = await onConfirm();
      if (result !== false) {
        setState("success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setState("failure");
      }
    } catch (error) {
      console.error(error);
      setState("failure");
    } finally {
      setIsLoading(false);
    }
  };

  const onCancelPress = () => {
    onOpenChange(false);
  };

  const HomeGuardianOptions = [
    { label: "Service Requests", value: "Service Requests" },
    { label: "Alerts", value: "Alerts" },
    { label: "SensorEvents", value: "SensorEvents" },
  ];

  const ServiceOptions = [
    { label: "Leads", value: "Leads" },
    { label: "Alerts", value: "Alerts" },
    { label: "SensorEvents", value: "SensorEvents" },
  ];

  const renderContent = () => {
    switch (state) {
      case "success":
        return (
          <SuccessContent
            onClose={() => onOpenChange(false)}
            integrationTitle={integrationTitle}
          />
        );
      case "failure":
        return (
          <ErrorContent
            onRetry={() => {
              setState("initial");
              setIsLoading(false);
            }}
            onClose={() => onOpenChange(false)}
            integrationTitle={integrationTitle}
          />
        );
      default:
        return (
          <>
            <YStack gap="$2" width="100%">
              <Dialog.Title
                fontSize="$3"
                fontWeight="normal"
                lineHeight="$1"
                textAlign="left"
                {...titleProps}
              >
                {title}
              </Dialog.Title>

              {description && (
                <Dialog.Description
                  fontSize="$4"
                  textAlign="left"
                  color="$white10"
                  {...descriptionProps}
                >
                  {description}
                </Dialog.Description>
              )}
            </YStack>
            <XStack
              alignItems="flex-start"
              justifyContent="center"
              width="100%"
              gap="$4"
            >
              <YStack gap="$2" flexGrow={1} flexBasis={0}>
                <Label fontSize="$3" lineHeight="$2" color="$white10">
                  Home Guardian
                </Label>
                <Select
                  options={HomeGuardianOptions}
                  value={"Service Requests"}
                  onValueChange={() => {}}
                />
              </YStack>
              <YStack justifyContent="center" height="100%" paddingTop="$4">
                <ArrowRightLeft color={tokens.color.white} size={20} />
              </YStack>
              <YStack gap="$2" flexGrow={1} flexBasis={0}>
                <Label fontSize="$3" lineHeight="$2" color="$white10">
                  {integrationTitle || "ServiceTitan"}
                </Label>
                <Select
                  options={ServiceOptions}
                  value={"Leads"}
                  onValueChange={() => {}}
                />
              </YStack>
            </XStack>
            <XStack
              gap="$3.5"
              alignItems="center"
              justifyContent="flex-end"
              width="100%"
            >
              <OutlinedButton
                rounded
                onPress={onCancelPress}
                {...cancelButtonProps}
              >
                {cancelButtonText ?? t("common.cancel")}
              </OutlinedButton>
              <GradientButton
                rounded
                loading={loading}
                onPress={onConfirmPress}
                {...confirmButtonProps}
              >
                {confirmButtonText ?? t("integrations.authorize")}
              </GradientButton>
            </XStack>
          </>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={480}
          maxWidth="100%"
          gap="$5"
          alignItems="center"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          {renderContent()}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default ConfigureIntegrationDialog;
